import { InputText } from 'primereact/inputtext'

import { cap } from '../../../State'
import { translations } from '../../../translations'
import { CALL_TYPES, EMAIL_FLOW_CALL_TYPES } from '../constants'
import { CollapsibleSection } from './CollapsibleSection'

const language = cap.language.state || 'IT'
const t = translations[language] || translations.IT

/**
 * Sezione parametri specifici per canale di comunicazione.
 * Mostra campi diversi in base al tipo di chiamata selezionato.
 * Layout 2 colonne per garantire visibilità completa dei valori.
 * @param {Object} props
 * @param {Object} props.config - Stato corrente della configurazione
 * @param {Function} props.setField - Funzione per aggiornare un campo semplice
 * @param {Function} props.setNested - Funzione per aggiornare un campo annidato
 */
export function ChannelParamsSection({ config, setField, setNested }) {
  const isMidOffice = config.callType === 'sendMessageMidOffice'
  const isWhatsApp = config.callType === 'whatsappMessage'
  // Tutti i flussi email per cliente (Frigerio, Gattinoni, Goelett) usano gli stessi
  // campi: prima Goelett non era incluso e i suoi riferimenti mail — obbligatori lato
  // API — non erano modificabili se non caricando la config client.
  const isEmailFlow = EMAIL_FLOW_CALL_TYPES.includes(config.callType)

  if (!isMidOffice && !isWhatsApp && !isEmailFlow) return null

  const callTypeLabel = CALL_TYPES.find((ct) => ct.value === config.callType)?.label

  return (
    <CollapsibleSection
      id="channel-params"
      title={t.dbcChannelParams || 'Parametri Canale'}
      icon="pi pi-sliders-h"
      badge={
        <span className="nbadge nbadge--info">
          {isMidOffice && 'MidOffice'}
          {isWhatsApp && 'WhatsApp'}
          {isEmailFlow && (callTypeLabel?.replace(/\s*Flow.*$/, '') || 'Email')}
        </span>
      }
    >
      <div className="debug-params-grid nora-test-params">
        {isMidOffice && (
          <>
            <div className="nfield" style={{ gridColumn: 'span 6' }}>
              <label>{t.dbcIdConversazione || 'ID Conversazione BTM'}</label>
              <InputText
                value={config.referencesBtmEmail?.idConversazione || ''}
                onChange={(e) => setNested('referencesBtmEmail', 'idConversazione', e.target.value)}
                placeholder="es. 1XM2B7TY"
              />
            </div>
            <div className="nfield" style={{ gridColumn: 'span 6' }}>
              <label>{t.dbcCodiceCliente || 'Codice Cliente'}</label>
              <InputText
                value={config.codiceCliente || ''}
                onChange={(e) => setField('codiceCliente', e.target.value)}
                placeholder="Codice cliente"
              />
            </div>
            <div className="nfield" style={{ gridColumn: 'span 6' }}>
              <label>{t.dbcIsola || 'Isola'}</label>
              <InputText
                value={config.isola || ''}
                onChange={(e) => setField('isola', e.target.value)}
                placeholder="Isola"
              />
            </div>
          </>
        )}

        {isWhatsApp && (
          <>
            <div className="nfield" style={{ gridColumn: 'span 6' }}>
              <label>{t.dbcWhatsappFrom || 'WhatsApp From'}</label>
              <InputText
                value={config.referenceWhatsapp?.from || ''}
                onChange={(e) => setNested('referenceWhatsapp', 'from', e.target.value)}
                placeholder="es. +39123456789"
              />
            </div>
            <div className="nfield" style={{ gridColumn: 'span 6' }}>
              <label>{t.dbcWhatsappId || 'WhatsApp ID'}</label>
              <InputText
                value={config.referenceWhatsapp?.id || ''}
                onChange={(e) => setNested('referenceWhatsapp', 'id', e.target.value)}
                placeholder="ID messaggio"
              />
            </div>
          </>
        )}

        {isEmailFlow && (
          <>
            <div className="nfield" style={{ gridColumn: 'span 6' }}>
              <label>{t.dbcMailRefId || 'ID Conversazione Email'}</label>
              <div className="debug-field-with-action">
                <InputText
                  value={config.mailReferencesEmail?.idConversazione || ''}
                  onChange={(e) =>
                    setNested('mailReferencesEmail', 'idConversazione', e.target.value)
                  }
                  placeholder="es. 1XM2B7TY"
                />
                <button
                  type="button"
                  className="debug-action-btn"
                  title="Genera un ID conversazione nuovo"
                  onClick={() =>
                    setNested(
                      'mailReferencesEmail',
                      'idConversazione',
                      `TEST${Date.now().toString(36).toUpperCase()}`
                    )
                  }
                >
                  <i className="pi pi-refresh" style={{ fontSize: 12 }} />
                </button>
              </div>
            </div>
            <div className="nfield" style={{ gridColumn: 'span 6' }}>
              <label>{t.dbcMailRefIsola || 'Email Isola'}</label>
              <InputText
                value={config.mailReferencesEmail?.emailIsola || ''}
                onChange={(e) => setNested('mailReferencesEmail', 'emailIsola', e.target.value)}
                placeholder="email@isola.it"
              />
            </div>
            <div className="nfield" style={{ gridColumn: 'span 6' }}>
              <label>{t.dbcMailRefSubject || 'Subject Email'}</label>
              <InputText
                value={config.mailReferencesEmail?.subject || ''}
                onChange={(e) => setNested('mailReferencesEmail', 'subject', e.target.value)}
                placeholder="Oggetto email [AIC:xxx]"
              />
            </div>
            <div className="nfield" style={{ gridColumn: 'span 6' }}>
              <label>{t.dbcSubject || 'Subject'}</label>
              <InputText
                value={config.subject || ''}
                onChange={(e) => setField('subject', e.target.value)}
                placeholder="Oggetto"
              />
            </div>
            <div className="nfield" style={{ gridColumn: 'span 6' }}>
              <label>{t.dbcBody || 'Body'}</label>
              <InputText
                value={config.body || ''}
                onChange={(e) => setField('body', e.target.value)}
                placeholder="Corpo del messaggio (default: userInput)"
              />
            </div>
          </>
        )}
      </div>
    </CollapsibleSection>
  )
}
